import {
  FieldPath, FieldPathByValue, FieldValues, PathValue, useFormContext,
} from 'react-hook-form'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import axios, { AxiosError } from 'axios'
import { TraderType } from 'features/declaration/models/schemas/tradersSchema'
import { TRANSIT_PARTY_NAME_MAX_LENGTH } from 'shared/ncts/common/constants'
import { apiService, handleResponseData } from 'services/api.service'
import { IConsignmentDetail } from 'types/IConsignmentDetail'
import { ExternalApiError, ValidationError } from 'types/ValidationError'
import Input from 'components/p5/Input'
import DotLeafPaths from 'helpers/dot-leaf-paths'
import { blankAddress, blankContactPerson } from 'features/declaration/models/blankItems'
import { DEFAULT_FIELD_RULE, FieldRule } from 'features/declaration/models/fieldRules'

const TraderDiscriminator = {
  CONSIGNEE: 'consignee',
  CONSIGNOR: 'consignor',
  CARRIER: 'carrier',
  DECLARANT: 'declarant',
} as const

export type TraderTarget = keyof typeof TraderDiscriminator

function mapToTraderForm(content: IConsignmentDetail, identificationNumber: string, target: TraderTarget): TraderType {
  const streetAndNumber = content.street ?? ''

  return {
    name: content.name ?? '',
    id: content.id ?? null,
    identificationNumber: content.eori ?? identificationNumber,
    address: content.city ? {
      country: content.country ?? '',
      city: content.city ?? '',
      id: null,
      postcode: content.zip ?? '',
      addressLine: streetAndNumber,
      street: streetAndNumber,
      streetNumber: '',
    } : blankAddress,
    contactPerson: blankContactPerson,
    target,
  }
}

export function mergeEoriTrader(current: TraderType | null | undefined, found: TraderType): TraderType {
  return {
    ...current,
    ...found,
    id: current?.id ?? null,
    address: found.address && { ...found.address, id: current?.address?.id ?? null },
  } as TraderType
}

type TraderRelPath = DotLeafPaths<TraderType>

type TraderBasePath<TForm extends FieldValues> = FieldPathByValue<TForm, TraderType | null | undefined>

type EoriSearchProps<TForm extends FieldValues, TPath extends TraderBasePath<TForm>> = {
  scope: TPath
  traderTarget: TraderTarget
  fieldRule?: FieldRule
}

function EoriSearch<
  TForm extends FieldValues,
  TPath extends TraderBasePath<TForm>,
>({ scope, traderTarget, fieldRule = DEFAULT_FIELD_RULE }: Readonly<EoriSearchProps<TForm, TPath>>) {
  const commonCompanyPrefixes = ['Sabiedrība ar ierobežotu atbildību ', 'UŽDAROJI AKCINĖ BENDROVĖ ', 'Usługi Transportowe ']
  const [eoriLoading, setEoriLoading] = useState(false)
  const { t } = useTranslation()

  const { getValues, setValue } = useFormContext<TForm>()

  const field = (rel: TraderRelPath) => `${scope}.${rel}` as FieldPath<TForm>

  const updateFormField = (trader: TraderType) => {
    const updatedTrader = mergeEoriTrader(getValues(scope), trader)
    setValue(scope, updatedTrader as PathValue<TForm, typeof scope>, { shouldDirty: true })
  }

  const searchUsingEori = (searchQuery: string) => {
    setEoriLoading(true)
    if (searchQuery === '') {
      setEoriLoading(false)
      return
    }
    if (searchQuery.startsWith('GB')) {
      toast.error(t('eori.error', { context: 'GB' }))
      setEoriLoading(false)
      return
    }

    axios.get(
      apiService.getFullApiUrl('/eori'),
      {
        params: {
          eoriCode: searchQuery.replace(' ', '').toUpperCase(),
        },
      },
    ).then(handleResponseData).then((eoriConsignmentDetails: IConsignmentDetail) => {
      let companyName = eoriConsignmentDetails.name

      const containsTypicalPrefix = commonCompanyPrefixes.find((prefix) => companyName.toLowerCase().startsWith(prefix.toLowerCase()))

      if (containsTypicalPrefix !== undefined) {
        companyName = companyName.substring(containsTypicalPrefix.length).replaceAll('"', '')
      }

      if (companyName.length > TRANSIT_PARTY_NAME_MAX_LENGTH) {
        companyName = companyName.substring(0, TRANSIT_PARTY_NAME_MAX_LENGTH)
        toast.warning('Company name was too long and has been shortened. Please check and verify the name.')
      }

      updateFormField(mapToTraderForm(eoriConsignmentDetails, searchQuery, traderTarget))
    }).catch((error: AxiosError<ValidationError | ExternalApiError>) => {
      let errorCode = '500'
      if (error.response && 'errorCode' in error.response.data) {
        errorCode = error.response.data.errorCode ?? '400'
      }
      if (error.response && 'errors' in error.response.data) {
        errorCode = '400'
        if (Array.isArray(error.response.data.errors) && error.response.data.errors.length) {
          errorCode = error.response.data.errors.some((item) => item.reason === 'Pattern') ? 'pattern' : '400'
        }
      }
      toast.error(t('eori.error', { context: errorCode }), { autoClose: 3000 })
    })
      .finally(() => {
        setEoriLoading(false)
      })
  }

  return (
    <div>
      <div className="input-group">
        <Input
          <TForm>
          field={field('identificationNumber')}
          labelKey="declaration.p5.consignorIdentificationNr"
          type="text"
          placeholder={t('declaration.eoriId')}
          onKeyDown={(onKeyDownEvent) => {
            if (onKeyDownEvent.key === 'Enter') {
              searchUsingEori(onKeyDownEvent.currentTarget.value)
            }
          }}
          fieldRule={fieldRule}
        />
        <div className="input-group-append">
          <button
            type="button"
            className="btn btn-light h-100 border"
            onClick={() => {
              if (!eoriLoading) {
                searchUsingEori(getValues(scope).identificationNumber)
              }
            }}
            disabled={eoriLoading || fieldRule?.requirement === 'forbidden'}
          >
            {
              eoriLoading
              && <i className="fa fa-spinner-third fa-spin" />
            }
            {
              !eoriLoading
              && <i className="fal fa-search" />
            }
          </button>
        </div>
      </div>

    </div>
  )
}

export default EoriSearch
